import React, { useMemo } from "react";
import { getPosFromKey } from "../chessUtils/util";
import { Pos } from "./types";

// Counter for generating stable unique IDs within a session
let arrowIdCounter = 0;

type ArrowProps = {
  fromPos: Pos;
  toPos: Pos;
  color?: string;
  opacity?: number;
  arrowId?: string;
};

function Arrow({
  fromPos,
  toPos,
  color = "#333333",
  opacity = 0.3,
  arrowId,
}: ArrowProps) {
  const dx = toPos[0] - fromPos[0];
  const dy = toPos[1] - fromPos[1];
  const angle = Math.atan2(dy, dx);
  const xr = Math.cos(angle);
  const yr = Math.sin(angle);

  const shortenStart = 4.5; // Shorten from the starting square
  const shortenEnd = 1; // Shorten from the destination square

  // Scale the shortening factors
  const xs = xr * shortenStart;
  const ys = yr * shortenStart;
  const xe = xr * shortenEnd;
  const ye = yr * shortenEnd;

  // Scale the shortening factor
  // const xe = xr * shortenBy;
  // const ye = yr * shortenBy;

  // const xs = 0; // xr * 6.25;
  // const ys = 0; // yr * 6.25;

  const x1 = fromPos[0] * 12.5 + 12.5 / 2 + xs + "%";
  const y1 = fromPos[1] * 12.5 + 12.5 / 2 + ys + "%";

  const x2 = toPos[0] * 12.5 + 12.5 / 2 - xe + "%";
  const y2 = toPos[1] * 12.5 + 12.5 / 2 - ye + "%";

  // Use stable ID based on position or provided arrowId, not Math.random()
  const arrowhead = useMemo(() => {
    return (
      arrowId ||
      `arrowhead-${fromPos[0]}-${fromPos[1]}-${toPos[0]}-${
        toPos[1]
      }-${color.replace("#", "")}-${++arrowIdCounter}`
    );
  }, [fromPos, toPos, color, arrowId]);

  return (
    <React.Fragment>
      <defs>
        <marker
          id={arrowhead}
          orient="auto"
          markerWidth="2.5"
          markerHeight="3"
          refX="1.5"
          refY="1.5"
        >
          <path d="M0,0 V3 L2.2,1.5 Z" fill={color}></path>
        </marker>
        {/* <marker
          id={arrowhead}
          orient="auto"
          markerWidth={"4"}
          markerHeight={"4"}
          refX="2"
          refY="2"
        >
          <path d="M0,0 V4 L3,2 Z" fill={color}></path>
        </marker> */}
      </defs>
      <line
        stroke={color}
        strokeWidth="2%"
        // strokeLinecap="round"
        markerEnd={`url(#${arrowhead})`}
        opacity={opacity}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
      ></line>
    </React.Fragment>
  );
}

type CircleProps = {
  fromPos: Pos;
};

function Circle({ fromPos }: CircleProps) {
  const x1 = fromPos[0] * 12.5 + 12.5 / 2 + "%";
  const y1 = fromPos[1] * 12.5 + 12.5 / 2 + "%";

  return (
    <circle
      stroke="#333333"
      fill="none"
      strokeWidth="2%"
      opacity={0.3}
      cx={x1}
      cy={y1}
      r={"5%"}
    ></circle>
  );
}

function renderShapes(shapes) {
  return shapes.map((shape, i) => {
    if (
      shape.to == null ||
      (shape.to[0] == shape.from[0] && shape.to[1] == shape.from[1])
    ) {
      return (
        <Circle
          fromPos={shape.from}
          key={`circle-${shape.from[0]}-${shape.from[1]}-${i}`}
        />
      );
    } else if (
      shape.from != null &&
      shape.to != null &&
      shape.from != shape.to
    ) {
      return (
        <Arrow
          fromPos={shape.from}
          toPos={shape.to}
          key={`arrow-${shape.from[0]}-${shape.from[1]}-${shape.to[0]}-${shape.to[1]}-${i}`}
          arrowId={`shape-${i}`}
        />
      );
    }
  });
}

function renderBestMove(bestMove, playingWhite) {
  const posFrom = getPosFromKey(bestMove.from, playingWhite) as Pos;
  const posTo = getPosFromKey(bestMove.to, playingWhite) as Pos;

  return (
    <Arrow
      fromPos={posFrom}
      toPos={posTo}
      opacity={0.4}
      arrowId={`best-${bestMove.from}-${bestMove.to}`}
    />
  );
}

function renderChessTermArrows(chessTermArrows, playingWhite) {
  return chessTermArrows.map((chessTermArrow, i) => {
    const posFrom = getPosFromKey(chessTermArrow.from, playingWhite);
    const posTo = getPosFromKey(chessTermArrow.to, playingWhite);

    return (
      <Arrow
        key={`chess-term-${chessTermArrow.from}-${chessTermArrow.to}-${i}`}
        fromPos={posFrom}
        toPos={posTo}
        color="#e99235"
        opacity={0.75}
        arrowId={`term-${chessTermArrow.from}-${chessTermArrow.to}`}
      />
    );
  });
}

export default function SvgLayer({
  shapes = null,
  bestMove,
  chessTermArrows,
  playingWhite,
}) {
  return (
    <div className="absolute z-40 w-full h-full pointer-events-none">
      <svg width="100%" height="100%">
        {bestMove && renderBestMove(bestMove, playingWhite)}
        {shapes && renderShapes(shapes)}
        {chessTermArrows &&
          renderChessTermArrows(chessTermArrows, playingWhite)}
      </svg>
    </div>
  );
}
